<template>
    <span class="badge" :class="badgeClass" :title="title">
        {{ label }}
    </span>
</template>

<script setup>
import { computed } from 'vue';

/**
 * Render a 4-state streaming status pill (active / degraded / silent / stopped
 * / unknown). Used in admin Computer/edit and any other monitor surface.
 *
 * Admin sees the full three states; students see a coarser online/offline
 * rendering (handled upstream via dispatch role-filtering, not here).
 *
 * Reason text mappings — keep aligned with EventOutcome::fromStreamEvent()
 * on the PHP side (kind / details.reason / details.kind).
 */
const props = defineProps({
    kind: {
        type: String,
        required: true,
        validator: (v) => ['webcam', 'screen', 'camera'].includes(v),
    },
    status: {
        type: String,
        default: 'unknown',
        validator: (v) => ['active', 'degraded', 'silent', 'stopped', 'unknown'].includes(v),
    },
    reason: {
        type: String,
        default: null,
    },
});

const KIND_LABEL = {
    webcam: 'Webcam',
    screen: 'Tela',
    camera: 'Câmera',
};

const REASON_PT = {
    silent: 'sem dados há mais de 30s',
    disconnect: 'desconectado',
    error: 'erro no produtor',
    frame_stall: 'quadro parado',
    low_fps: 'baixa taxa de quadros',
};

function reasonText(reason) {
    if (!reason) return '';
    return REASON_PT[reason] ?? reason;
}

const label = computed(() => {
    const kind = KIND_LABEL[props.kind] ?? 'Stream';
    switch (props.status) {
        case 'active':
            return `${kind}: Conectada`;
        case 'degraded':
            return `${kind}: Conectada, mas ${reasonText(props.reason)}`;
        case 'silent':
        case 'stopped':
            return `${kind}: Desconectada${props.reason ? ' — ' + reasonText(props.reason) : ''}`;
        default:
            return `${kind}: —`;
    }
});

const badgeClass = computed(() => {
    switch (props.status) {
        case 'active':
            return 'bg-success';
        case 'degraded':
            return 'bg-warning text-dark';
        case 'silent':
        case 'stopped':
            return 'bg-danger';
        default:
            return 'bg-secondary';
    }
});

const title = computed(() => {
    return props.reason
        ? `Estado: ${props.status} — motivo: ${props.reason}`
        : `Estado: ${props.status}`;
});
</script>
